import React, { PropsWithChildren, CSSProperties, useEffect, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import ReactFocusLock, { MoveFocusInside } from 'react-focus-lock';

import x from '../images/close.svg';
import back from '../images/arrow-left-back.svg';
import { ModalType, useModal, stringToModalType } from '../state/ModalProvider';
import useSearchParams from '../hooks/useSearchParams';

import Portal from './Portal';

/**
 * A modal window with automatic open and close handling.
 * 
 * This component can be used in two ways: Uncontrolled (preferred) and Controlled.  
 * - Uncontrolled: The component manages its own state via ModalContext. Provide a "type" prop. The modal can be opened and closed via the "useModal" hook or query params.  
 * - Controlled: The parent component manages the modal's state. Provide "visible" and "onClose" prop. The modal can be opened and closed by updating the "visible" prop.  
 * 
 * Note: It is invalid to provide both "type" and "visible" props, as state must be managed in one place.  
*/

const FadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const Backdrop = styled.button`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 1rem;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 1000;
  cursor: default;
  animation: ${FadeIn} 150ms ease-in-out 1 forwards;
`;

const ModalContainer = styled.button`
  background-color: #ededed;
  padding: 40px 20px 60px;
  border-radius: 20px;
  max-width: 550px;
  width: 100%;
  margin: auto;
  position: relative;
  z-index: 1001;
  cursor: default;
`;

const CloseBtn = styled.button`
  margin-left: auto;
  &::before {
    content: '';
    display: block;
    height: 21px;
    width: 21px;
    background-image: url(${x});
    background-repeat: no-repeat;
    background-size: cover;
  }
`;

const Controls = styled.div`
  margin: -20px 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = styled.button`
  &::before {
    content: '';
    display: block;
    height: 21px;
    width: 21px;
    background-image: url(${back});
    background-repeat: no-repeat;
    background-size: cover;
  }
`;

const StyledContent = styled.div<{ styledContentStyle?: string | CSSProperties }>`
  > :last-child {
    margin-bottom: 0;
  }

  ${({ styledContentStyle }) => typeof styledContentStyle === 'string' && css`${styledContentStyle}`}
`;

interface ModalProps extends React.HTMLAttributes<HTMLDivElement>, PropsWithChildren {
  type?: ModalType,
  visible?: boolean,
  showClose?: boolean,
  onClose?: () => void,
  showBack?: boolean,
  onBack?: () => void,
}

export interface UncontrolledProps extends ModalProps {
  visible?: never,
  type: ModalProps['type'],
  onClose?: ModalProps['onClose'],
}

export interface ControlledProps extends ModalProps {
  type?: never,
  visible: ModalProps['visible'],
  onClose: ModalProps['onClose'],
}

export type Props = UncontrolledProps | ControlledProps;

const Modal = ({
  type,
  visible,
  showClose = true,
  onClose = () => {},
  showBack = false,
  onBack = () => {},
  style,
  children,
}: Props) => {
  const { activeModals, closeModal, openModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();

  const close = useCallback(() => {
    if (!showClose) return;
    if (type) closeModal(type);
    onClose();
  }, [showClose, closeModal, type, onClose]);

  const handleClosePressed = () => close();
  const handleBackPressed = () => onBack();
  const handleBackdropPressed = () => close();
  const handleBackdropKeydown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') close();
  };
  const handleContainerPressed = (e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation();
  const handleContainerKeydown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Escape') e.stopPropagation();
  };

  useEffect(() => {
    const modalKey = 'modal';
    const param = searchParams.get(modalKey);
    const modalType = param ? stringToModalType(param) : null;
    if (modalType) {
      openModal(modalType);
      searchParams.delete(modalKey);
      setSearchParams(searchParams, true);
    }
  }, [searchParams, setSearchParams, openModal]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [close]);

  if (type && typeof visible === 'boolean') {
    throw new Error('Modal: cannot use this component with both a type and visible prop.');
  }
  return ((type && type === activeModals[0]) || visible) && (
    <Portal id="modal-menu">
      <ReactFocusLock>
        <Backdrop
          onClick={handleBackdropPressed}
          onKeyDown={handleBackdropKeydown}
          tabIndex={0}
          data-testid="ModalBackdrop"
          data-type={type}
        >
          <ModalContainer
            onClick={handleContainerPressed}
            onKeyDown={handleContainerKeydown}
            tabIndex={0}
            data-testid="ModalContainer"
          >

            <MoveFocusInside>
              <Controls>
                {showBack && (
                  <BackButton onClick={handleBackPressed} aria-label="Back" />
                )}
                {showClose && (
                  <CloseBtn onClick={handleClosePressed} aria-label="Close" />
                )}
              </Controls>

              <StyledContent style={style} data-testid="StyledContent">
                {children}
              </StyledContent>
            </MoveFocusInside>
          </ModalContainer>
        </Backdrop>
      </ReactFocusLock>
    </Portal>
  );
};

export { Modal };
